import { useEffect, useState } from "react";
import { Box, Pagination, Stack, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import DocumentCard from "./components/DocumentCard";
import { useFetchContext } from "../../contexts/FetchContext";
import { IDataPagination } from "../../interfaces";
import { DocumentType, DocumentsData } from "../../interfaces/document";
import { PageContainer } from "../shared/components/PageContainer";
import { useNavigate } from "react-router-dom";
import TableFilter from "../shared/components/TableFilter";
import DocumentFilterContent from "./components/DocumentFilterContent";
import dayjs from "dayjs";
import NoResult from "../shared/components/NoResult";

export const Documents = () => {
  const { state, fetchData } = useFetchContext();
  const navigate = useNavigate();

  const [data, setData] = useState<IDataPagination<DocumentsData> | null>(null);
  const [page, setPage] = useState(1);
  const urlParams = new URLSearchParams(window.location.search);
  const typeParam = urlParams.get("type");
  const statusParam = urlParams.get("status");
  const [status, setStatus] = useState(statusParam);
  const [date, setDate] = useState<Date | null>(null);
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    undefined
  );

  const [seeBadge, setSeeBadge] = useState<boolean>(false);
  const [lastTypeParm, setLastTypeParm] = useState(typeParam);

  useEffect(() => {
    if (typeParam !== lastTypeParm) {
      setLastTypeParm(typeParam);
    }
  }, [lastTypeParm, typeParam]);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        let apiUrl = "/documents?pageSize=5";

        if (page !== undefined) {
          apiUrl += `&page=${page}`;
        }

        if (searchKeyword !== undefined) {
          apiUrl += `&searchKeyword=${searchKeyword}`;
        }

        if (typeParam !== undefined) {
          apiUrl += `&type=${typeParam}`;
        }

        if (status !== undefined) {
          apiUrl += `&status=${status}`;
        }

        if (date !== null) {
          apiUrl += `&date=${dayjs(date).format("YYYY-MM-DD")}`;
        }

        apiUrl += "&orderBy=id";
        if (order !== undefined) {
          apiUrl += `&orderType=${order}`;
        }

        const response = await fetchData(apiUrl);
        setData(response?.data);
      } catch (error) {
        console.log(error);
        // Gestisci gli errori qui
      }
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, lastTypeParm, status, order, date, searchKeyword]);

  const handleSearchChange = (searchTerm: string) => {
    setSearchKeyword(searchTerm);
  };

  const handleSortChange = (state: "ASC" | "DESC") => {
    setOrder(state);
  };

  const onApplyFilter = (state: string | null, date: Date | null) => {
    setStatus(state);
    setDate(date);
    setSeeBadge(true);
  };

  const onBadgeClick = () => {
    setStatus(statusParam);
    setDate(null);
    setSeeBadge(false);
  };

  return (
    <PageContainer>
      {state.isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Stack>
          <Grid container>
            <Grid item xs={12} sx={{ pb: 2 }}>
              <TableFilter
                sortOrder={order}
                onSearchChange={handleSearchChange}
                onSortChange={handleSortChange}
                filterContent={
                  <DocumentFilterContent
                    onApplyFilter={onApplyFilter}
                    status={status}
                    date={date}
                  />
                }
                searchKeyword={searchKeyword}
                onBadgeClick={onBadgeClick}
                seeBadge={seeBadge}
              />
            </Grid>
            {data?.items?.map((el, index) => (
              <Grid item xs={12} key={index} sx={{ pb: 2 }}>
                <DocumentCard
                  elements={el}
                  onClick={() => {
                    if (typeParam === DocumentType.ACCEPTANCE) {
                      navigate("/acceptance?uuid=" + el.uuid);
                    }
                    if (typeParam === DocumentType.DISPATCH) {
                      navigate("/dispatch?uuid=" + el.uuid);
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {data?.total ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pb: 15,
              }}
            >
              <Pagination
                count={data?.totalPages ?? 0}
                size="small"
                onChange={(e, page) => setPage(page)}
                page={page}
              />
            </Box>
          ) : (
            <NoResult checkFilter={seeBadge} />
          )}
        </Stack>
      )}
    </PageContainer>
  );
};

export default Documents;
